<template>
  <div v-rect-loading="loading" class="question-editor">
    <div class="header">
      <div class="title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="formEditor"
        :labelWidth="labelWidth"
        :model="question"
        :formLines="formLines"
      >
        <template #type="{ lineName, line }">
          <el-select v-model="question[lineName]" :disabled="isEditMode" :placeholder="line.placeholder" @change="handleTypeChange">
            <el-option
              v-for="(item, key) in questionType"
              :key="key"
              :label="$t(item.i18nKey)"
              :value="item.value"
            />
          </el-select>
        </template>
        <template #options="{ lineName, line }">
          <div class="option-list-container">
            <draggable
              v-model="question[lineName]"
              tag="el-collapse"
              handle=".drag-handler"
              animation="200"
              group="description"
              ghostClass="ghost"
              class="option-list"
              :component-data="collapseConfig">
              <el-collapse-item
                v-for="(item, index) in question[lineName]"
                class="option-item"
                :key="index"
                :name="item._vid">
                <template #title>
                  <div class="collapse-title-wrapper">
                    <div class="option-item-title">
                      <div class="item-name">{{ $t(`${i18nPrefix}.label.optionNo`, { number: index + 1 }) }}</div>
                      <div class="item-preview">
                        <span>{{ item.content_cn || '-' }}</span>
                        <span>{{ item.content_tc || '-' }}</span>
                        <span>{{ item.content_en || '-' }}</span>
                      </div>
                    </div>
                    <div class="option-operate" @click.stop="() => {}">
                      <div class="drag-handler">
                        <i class="iconfont iconMore"></i>
                      </div>
                      <div class="add-btn">
                        <i class="el-icon-circle-plus-outline" @click="addOptionItem(index)"></i>
                      </div>
                      <div v-if="question[lineName].length > 1" class="remove-btn">
                        <i class="el-icon-remove-outline" @click="removeOptionItem(index)"></i>
                      </div>
                    </div>
                  </div>
                </template>
                <WrappedFrom
                  class="option-item-form"
                  ref="optionItemForm"
                  :labelWidth="optionNameLabelWidth"
                  :model="question[lineName][index]"
                  :formLines="line.subLine"
                >
                </WrappedFrom>
              </el-collapse-item>
            </draggable>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { ONBOARDING_QUESTION_TYPE, ONBOARDING_QUESTION_SCOPED_TYPE } from '@/constant/common'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import draggable from 'vuedraggable'
import { mapActions } from 'vuex'

export default {
  components: {
    WrappedFrom,
    draggable
  },
  data () {
    return {
      allQuestionType: ONBOARDING_QUESTION_TYPE,
      scopedType: ONBOARDING_QUESTION_SCOPED_TYPE,
      i18nPrefix: 'admin.company.onboarding',
      questionType: {},
      question: {
        options: [],
        type: null
      },
      placeholderHidden: false,
      optionHidden: true,
      linkHidden: true,
      activeOptionItems: [],
      optionsVid: 1,
      linkOptionId: null,
      loading: false
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isScopedType () {
      return this.scopedType.includes(this.question.type) || this.question.type === this.allQuestionType.LOCATION.value
    },
    formLines () {
      const linkRule = { required: true, message: this.$t(`${this.i18nPrefix}.message.agreementLinkRequired`) }
      const contentRule = { required: true, message: this.$t(`${this.i18nPrefix}.message.questionNameRequired`) }
      const optionsRule = { required: true, message: this.$t(`${this.i18nPrefix}.message.optionNameRequired`), trigger: 'change' }
      const contentLines = {}
      const placeholderLines = {}
      const localeSequence = ['cn', 'tc', 'en']
      if (this.isScopedType) {
        _.forEach(this.scopedType, typeItem => {
          const typeName = typeItem.toLowerCase()
          _.forEach(localeSequence, item => {
            contentLines[`${typeName}_content_${item}`] = {
              label: this.$t(`${this.i18nPrefix}.label.name.${typeName}.${item}`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
              rules: contentRule
            }
            placeholderLines[`${typeName}_placeholder_${item}`] = {
              label: this.$t(`${this.i18nPrefix}.label.placeholder.${typeName}.${item}`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.placeholder`),
              hidden: this.placeholderHidden
            }
          })
        })
      } else {
        _.forEach(localeSequence, item => {
          contentLines[`content_${item}`] = {
            label: this.$t(`${this.i18nPrefix}.label.name.${item}`),
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionName`),
            rules: contentRule
          }
          placeholderLines[`placeholder_${item}`] = {
            label: this.$t(`${this.i18nPrefix}.label.placeholder.${item}`),
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.placeholder`),
            hidden: this.placeholderHidden
          }
        })
      }

      const lines = {
        type: {
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.questionType`),
          contentSlot: 'type',
          rules: { required: true, message: this.$t(`${this.i18nPrefix}.message.questionTypeRequired`) }
        },
        ...contentLines,
        ...placeholderLines,
        link_cn: {
          label: this.$t(`${this.i18nPrefix}.label.agreementLink.cn`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.agreementLink`),
          hidden: this.linkHidden,
          rules: linkRule
        },
        link_tc: {
          label: this.$t(`${this.i18nPrefix}.label.agreementLink.tc`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.agreementLink`),
          hidden: this.linkHidden,
          rules: linkRule
        },
        link_en: {
          label: this.$t(`${this.i18nPrefix}.label.agreementLink.en`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.agreementLink`),
          hidden: this.linkHidden,
          rules: linkRule
        },
        options: {
          label: this.$t(`${this.i18nPrefix}.label.name.cn`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
          hidden: this.optionHidden,
          contentSlot: 'options',
          rules: optionsRule,
          subLine: {
            content_cn: {
              label: this.$t(`${this.i18nPrefix}.label.name.cn`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            },
            content_tc: {
              label: this.$t(`${this.i18nPrefix}.label.name.tc`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            },
            content_en: {
              label: this.$t(`${this.i18nPrefix}.label.name.en`),
              placeholder: this.$t(`${this.i18nPrefix}.placeholder.optionName`),
              rules: optionsRule
            }

          }
        }
      }
      return lines
    },
    collapseConfig () {
      const config = {
        props: {
          value: this.activeOptionItems
        },
        on: {
          change: this.handleCollapseChange
        }
      }
      return config
    },
    form () {
      return this.$refs.formEditor
    },
    labelWidth () {
      if (this.isScopedType) {
        if (this.$i18n.locale === 'en') {
          return '330px'
        } else {
          return '240px'
        }
      }
      if (this.$i18n.locale === 'en') {
        return '270px'
      } else {
        return '142px'
      }
    },
    optionNameLabelWidth () {
      if (this.$i18n.locale === 'en') {
        return '210px'
      } else {
        return '120px'
      }
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}.title.question.${this.isEditMode ? 'edit' : 'add'}`)
    }
  },
  created () {
    this.questionType = _.filter(this.allQuestionType, (value, key) => !this.scopedType.includes(key))
    if (this.isEditMode) {
      this.getQuestionData()
    }
  },
  methods: {
    ...mapActions('onboardingManagement', ['getQuestionById', 'createQuestion', 'updateQuestion']),
    async getQuestionData () {
      this.loading = true
      try {
        const questionId = this.$route.params.questionId
        const resp = await this.getQuestionById(questionId)
        if (resp) {
          const question = resp.question
          if (question.type === this.allQuestionType.LINK.value) {
            const linkConfig = question.options[0] || {}
            question.link_cn = linkConfig.content_cn
            question.link_tc = linkConfig.content_tc
            question.link_en = linkConfig.content_en
            this.linkOptionId = linkConfig.id
            question.options = []
          }
          if (!question.options) {
            question.options = []
          }
          _.forEach(question.options, item => {
            item._vid = ++this.optionsVid
          })
          if (question.type === this.allQuestionType.LOCATION.value) {
            _.forEach(this.scopedType, item => {
              const typeName = item.toLowerCase()
              const questionItem = question[typeName]
              _.forEach(questionItem, (value, key) => {
                question[`${typeName}_${key}`] = value
              })
            })
          }
          this.question = question
          this.handleTypeChange(question.type)
        }
      } finally {
        this.loading = false
      }
    },
    handleTypeChange (type) {
      if (type === this.allQuestionType.LINK.value) {
        this.placeholderHidden = true
        this.linkHidden = false
      } else {
        this.placeholderHidden = false
        this.linkHidden = true
      }
      if (type === this.allQuestionType.SINGLE.value) {
        this.optionHidden = false
        if (this.question.options && this.question.options.length === 0) {
          this.question.options = []
          this.addOptionItem(0)
        }
      } else {
        this.optionHidden = true
      }
      this.$nextTick(() => this.form.clearValidate())
    },
    handleCollapseChange (activeNames) {
      this.activeOptionItems = activeNames
    },
    addOptionItem (index) {
      this.optionsVid += 1
      this.question.options.splice(index + 1, 0, {
        _vid: this.optionsVid,
        content_en: null,
        content_tc: null,
        content_cn: null
      })
      this.activeOptionItems.push(this.optionsVid)
    },
    removeOptionItem (index) {
      this.question.options.splice(index, 1)
    },
    handleCancel () {
      this.$router.go(-1)
    },
    async handleSave () {
      let result = false
      result = await this.form.submit()
      if (this.question.type === ONBOARDING_QUESTION_TYPE.SINGLE.value) {
        const optionResults = await Promise.all(this.$refs.optionItemForm.map((form) => form.submit()))
        result = _.every(optionResults, (item) => item) && result
      }
      if (!result) {
        return
      }
      this.loading = true
      try {
        let question = {
          content_cn: this.question.content_cn,
          content_tc: this.question.content_tc,
          content_en: this.question.content_en,
          type: this.question.type,
          placeholder_cn: this.question.placeholder_cn,
          placeholder_tc: this.question.placeholder_tc,
          placeholder_en: this.question.placeholder_en
        }
        if (question.type === this.allQuestionType.LINK.value) {
          question.options = [
            {
              content_cn: this.question.link_cn,
              content_tc: this.question.link_tc,
              content_en: this.question.link_en,
              order: 1
            }
          ]
          if (this.isEditMode) {
            question.options[0].id = this.linkOptionId
          }
        } else if (question.type === this.allQuestionType.LOCATION.value) {
          const scopedQuestion = {}
          _.forEach(this.question, (value, key) => {
            _.forEach(this.scopedType, item => {
              const typeName = item.toLowerCase()
              if (key.startsWith(`${typeName}_`)) {
                const questionKey = key.replace(`${typeName}_`, '')
                const questionItem = scopedQuestion[typeName] || {}
                questionItem[questionKey] = value
                scopedQuestion[typeName] = questionItem
              }
            })
          })
          question = {
            type: this.question.type,
            ...scopedQuestion
          }
        } else {
          question.options = _.map(this.question.options, (item, index) => {
            return {
              id: item.id,
              content_cn: item.content_cn,
              content_tc: item.content_tc,
              content_en: item.content_en,
              order: index + 1
            }
          })
        }
        let saveFunction = null
        if (this.isEditMode) {
          question.id = this.question.id
          saveFunction = this.updateQuestion
        } else {
          saveFunction = this.createQuestion
        }
        const resp = await saveFunction(question)
        if (resp) {
          this.$router.push({ path: '/v-admin/onboarding-management/question/list' })
        }
      } finally {
        this.loading = false
      }
    }
  },
  watch: {

  }
}
</script>

<style lang="scss" scoped>
.question-editor {
  .header {
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .option-list-container {
    .option-list {
      min-height: 20px;

      .option-item {
        width: 100%;
        padding-right: 50px;
        box-sizing: content-box;
        position: relative;

        &.ghost {
          opacity: 0.5;

          ::v-deep {
            .el-collapse-item__wrap, .el-collapse-item__header {
              background-color: #c8ebfb;
            }
          }
        }

        ::v-deep .el-collapse-item__content {
          padding-bottom: 6px;
        }

        .collapse-title-wrapper {
          width: calc(100% - 20px);
          height: 100%;

          .option-item-title {
            display: flex;
            width: 100%;
            overflow: hidden;

            .item-name {
              margin-right: 10px;
            }

            .item-preview {
              flex: 1;
              text-align: center;
              overflow: hidden;
              word-break: keep-all;
              white-space: nowrap;
              text-overflow: ellipsis;

              span {
                height: 25px;
                line-height: 25px;
                padding: 0 5px;

                &:not(:last-child) {
                  border-right: 1px solid #ccc;
                }
              }
            }
          }

          .option-operate {
            display: flex;
            position: absolute;
            top: 2px;
            left: calc(100% - 50px);

            > div {
              margin-right: 5px;

              &:hover {
                color: #0096cc;

                i {
                  font-size: 22px;
                }
              }
            }

            i {
              font-size: 18px;
            }

            .drag-handler {
              display: none;
              cursor: grab;
            }
          }
        }

        .option-item-form {
          width: 100%;
          ::v-deep {
            .el-form-item {
              margin-bottom: 22px;
            }
          }
        }
      }
    }
  }

  .form-container {
    width: 700px;
    margin: 30px auto 0;

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
      }

      .save-btn {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
